/**
 * Utility for prefetching images to improve perceived load times.
 * Uses requestIdleCallback to avoid blocking main thread.
 */

/**
 * Prefetch images by loading them into the browser cache.
 * Uses requestIdleCallback when available for better performance.
 *
 * @param urls - Array of image URLs to prefetch (null/undefined values are filtered out)
 */
export function prefetchImages(urls: (string | null | undefined)[]): void {
  const validUrls = urls.filter((url): url is string => Boolean(url));

  validUrls.forEach(url => {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        () => {
          const img = new Image();
          img.src = url;
        },
        { timeout: 3000 }
      );
    } else {
      // Fallback for browsers without requestIdleCallback (Safari)
      setTimeout(() => {
        const img = new Image();
        img.src = url;
      }, 100);
    }
  });
}

/**
 * Prefetch a single image URL.
 * Useful for prefetching images as data becomes available.
 *
 * @param url - Image URL to prefetch
 */
export function prefetchImage(url: string | null | undefined): void {
  if (!url) return;
  prefetchImages([url]);
}
